import React, { FC, useContext } from 'react';
import get from 'lodash/get';
import styled from 'styled-components';

import { AlertInfo } from '@ad-scripts/presentation/components/common/alert';

import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import NodeSelector from 'src/presentation/components/pages/ivr/components/NodeSelector';
import PhoneNumberInput from 'src/presentation/components/forms/PhoneNumberInput';
import Textarea from 'src/presentation/components/forms/Textarea';
import {
  Content,
  Footer,
  Row,
  ScrollRow,
  MultiContentRow,
} from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import {
  NodeProps,
  SIDE_PANEL_ROW_TYPES,
  DESCRIPTION_PLACEHOLDER_TEXT,
  DESCRIPTION_LABEL_TEXT,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';

import UnionIcon from 'src/images/Union.svg';

const SmsNode: FC<NodeProps> = ({ attr, disabled, nodeList, onSelectNode, readOnly, selectedOptionIndex }) => {
  const { workingState } = useContext(FormContext);
  const currentNodeId = get(workingState, `${attr}.id`);

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid={`${attr}-name`}
            disabled={disabled}
            label="Name"
            description="The name you can refer to link to other nodes"
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid={`${attr}-description`}
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>

        <MultiContentRow>
          <Textarea
            attr={`${attr}.body`}
            data-aid={`${attr}.body`}
            description="Message that will get sent to your customer"
            disabled={disabled}
            label="SMS Body"
            readOnly={readOnly}
          />
          <StyledAlertInfo>
            <b>Heads up:&nbsp;</b>
            Make sure you include the opt-out keyword “STOP” in your text messages.{' '}
            <StyledLink target="_blank" rel="noopener noreferrer" href={'https://help.gladly.com/docs/sms-send-error'}>
              Learn why
              <img src={UnionIcon} />
            </StyledLink>
          </StyledAlertInfo>
        </MultiContentRow>

        <Row>
          <PhoneNumberInput
            attr={`${attr}.number`}
            data-aid={`${attr}.number`}
            description="Number that will receive the SMS. Default is the caller's number; can be static or a template definition"
            disabled={disabled}
            label="To Number"
            readOnly={readOnly}
          />
        </Row>
      </Content>

      <Footer>
        <Row>
          <NodeSelector
            searchable
            attr={`${attr}.nextId`}
            data-aid={`${attr}.nextId`}
            description="The next node in the IVR"
            disabled={disabled}
            label="Next Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </Row>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.ERROR_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.errorNodeId`}
            data-aid={`${attr}.errorNodeId`}
            description="When an error occurs while trying to send the SMS, go to this node"
            disabled={disabled}
            label="Error Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
      </Footer>
    </>
  );
};

const StyledAlertInfo = styled(AlertInfo)`
  line-height: 20px;
  > svg {
    margin-top: 3px;
  }
`;

export const StyledLink = styled.a`
  display: inline-flex;
  color: inherit;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }

  & > img {
    padding-left: 4px;
  }
`;

export default SmsNode;
